import type { ReactNode } from "react";
import type { PanelImperativeHandle } from "react-resizable-panels";

import { type RefObject, useLayoutEffect, useMemo } from "react";

import type { MainPanelConfig } from "../store/panels.store.types";

import { calculateTopPanelSize } from "../components/PanelsView.helpers";

interface UseVerticalPanelPairResizeProps {
    topConfig: MainPanelConfig;
    bottomConfig: MainPanelConfig;
    topASlot?: ReactNode;
    topBSlot?: ReactNode;
    bottomASlot?: ReactNode;
    bottomBSlot?: ReactNode;
    lastTopPanelSize: number | undefined;
    topPanelRef: RefObject<PanelImperativeHandle | null>;
    bottomPanelRef: RefObject<PanelImperativeHandle | null>;
    /** ID of the top panel element for direct DOM height capping */
    topPanelId: string;
    /** Collapsed height in pixels for the top panel header */
    topCollapsedHeightPixels: number;
    /** ID of the bottom panel element for direct DOM height capping */
    bottomPanelId: string;
    /** Collapsed height in pixels for the bottom panel header */
    bottomCollapsedHeightPixels: number;
}

/**
 * Resizes a vertical pair of panels (top + bottom) so each can minimize/expand independently.
 * Bottom is applied first so that when both are minimized, the top collapses correctly.
 * Returns whether the bottom is minimized/closed for maxHeight so it stays header-only.
 */
export function useVerticalPanelPairResize({
    topConfig,
    bottomConfig,
    topASlot,
    topBSlot,
    bottomASlot,
    bottomBSlot,
    lastTopPanelSize,
    topPanelRef,
    bottomPanelRef,
    topPanelId,
    topCollapsedHeightPixels,
    bottomPanelId,
    bottomCollapsedHeightPixels,
}: UseVerticalPanelPairResizeProps) {
    const topSize = useMemo(
        () =>
            calculateTopPanelSize({
                lastSize: lastTopPanelSize,
                topConfig,
                bottomConfig,
                topASlot,
                topBSlot,
                bottomASlot,
                bottomBSlot,
            }),
        [lastTopPanelSize, topConfig, bottomConfig, topASlot, topBSlot, bottomASlot, bottomBSlot],
    );

    const isTopMinimizedOrClosed =
        (topConfig.selectedTab === "a" ? topConfig.a.isMinimized : topConfig.b.isMinimized) ||
        (topConfig.a.isClosed && topConfig.b.isClosed);

    const isBottomMinimizedOrClosed =
        (bottomConfig.selectedTab === "a" ? bottomConfig.a.isMinimized : bottomConfig.b.isMinimized) ||
        (bottomConfig.a.isClosed && bottomConfig.b.isClosed);

    const bottomSize = isBottomMinimizedOrClosed ? 0 : 100 - topSize;

    useLayoutEffect(() => {
        bottomPanelRef.current?.resize(`${bottomSize}%`);
        topPanelRef.current?.resize(`${topSize}%`);
        // oxlint-disable-next-line exhaustive-deps -- refs are stable, only resize when size/config changes
    }, [topSize, bottomSize, topConfig, bottomConfig]);

    // v4 applies `style` to a nested div, not the outer flex item. To cap a panel's
    // visual height when minimized (showing only the header), we directly set
    // maxHeight + overflow on the outer panel element via its DOM id.
    // This matches v3's behavior where style={{ maxHeight }} was on the flex item.
    useLayoutEffect(() => {
        const el = document.getElementById(topPanelId);
        if (!el) return;

        if (isTopMinimizedOrClosed && topCollapsedHeightPixels > 0) {
            el.style.maxHeight = `${topCollapsedHeightPixels}px`;
            el.style.overflow = "hidden";
        } else {
            el.style.maxHeight = "";
            el.style.overflow = "";
        }

        return () => {
            el.style.maxHeight = "";
            el.style.overflow = "";
        };
    }, [topPanelId, isTopMinimizedOrClosed, topCollapsedHeightPixels]);

    useLayoutEffect(() => {
        const el = document.getElementById(bottomPanelId);
        if (!el) return;

        if (isBottomMinimizedOrClosed && bottomCollapsedHeightPixels > 0) {
            el.style.maxHeight = `${bottomCollapsedHeightPixels}px`;
            el.style.overflow = "hidden";
        } else {
            el.style.maxHeight = "";
            el.style.overflow = "";
        }

        return () => {
            el.style.maxHeight = "";
            el.style.overflow = "";
        };
    }, [bottomPanelId, isBottomMinimizedOrClosed, bottomCollapsedHeightPixels]);

    return { isBottomMinimizedOrClosed };
}
